import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, RefreshCw, UserX, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const hhmm = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function Metric({ label, value, sub, color, testid }) {
  return (
    <Card className="p-3.5 border border-border" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-display text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </Card>
  );
}

export default function RehearsalReport() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (manual) => {
    if (manual) setBusy(true);
    try {
      const { data } = await api.get("/election-day/rehearsal-report");
      setD(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = (path, name) => {
    const token = localStorage.getItem("imeak_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = name;
        a.click();
      })
      .catch(() => toast.error("İndirme başarısız — kayıt bulunamadı olabilir"));
  };

  if (!d) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Karne hazırlanıyor...</div>;

  const t = d.totals;

  return (
    <div className="space-y-5" data-testid="rehearsal-report">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h3 className="font-display font-semibold text-base md:text-lg">Prova Karnesi</h3>
          <p className="text-xs text-muted-foreground">
            Test modunda yapılan tüm işaretlemeler. Prova bitse de 10 Ekim 22:00'deki otomatik
            sıfırlamaya kadar burada görünür.
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={busy} data-testid="rr-refresh">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" data-testid="rr-export"
            onClick={() => download("/election-day/rehearsal-report/export", "PROVA_KARNESI.xlsx")}>
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" data-testid="rr-cards"
            onClick={() => download("/election-day/officer-cards", "SANDIK_GOREVLISI_KARTLARI.pdf")}>
            <Download className="h-4 w-4 mr-1" /> Tüm Görevli Kartları (PDF)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="rr-metrics">
        <Metric label="Toplam İşaretleme" value={t.attempts} sub={`${t.active} geçerli · ${t.undone} geri alınan`} color="#0F2C59" testid="rr-attempts" />
        <Metric label="Deneyen Kişi" value={t.people} color="#16A34A" testid="rr-people" />
        <Metric label="Denenen Komite" value={`${t.committees_tried}/${t.committees_total}`} color="#0369A1" testid="rr-committees" />
        <Metric label="Atanmış Görevli" value={t.officers_total} color="#7C3AED" testid="rr-officers" />
        <Metric label="Hiç Denemeyen Görevli" value={t.officers_not_tried} color="#DC2626" testid="rr-not-tried" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-0 border border-border overflow-hidden" data-testid="rr-people-table">
          <div className="p-4 pb-2">
            <h4 className="font-display font-semibold text-base">Kişi bazlı</h4>
          </div>
          <ScrollArea className="h-[340px]">
            {d.people.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">Henüz kimse denemedi.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/95 text-xs">
                  <tr className="text-left">
                    {["Kullanıcı", "İşaretleme", "Geçerli", "Komite", "Son işlem"].map((h) => (
                      <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.people.map((p, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`rr-person-${i}`}>
                      <td className="px-2 py-1.5">
                        {p.name}
                        <span className="block text-[11px] text-muted-foreground">{p.role_label}</span>
                      </td>
                      <td className="px-2 py-1.5 tabular-nums font-semibold">{p.attempts}</td>
                      <td className="px-2 py-1.5 tabular-nums text-emerald-700 dark:text-emerald-400">{p.active}</td>
                      <td className="px-2 py-1.5 text-xs">{p.committees.map((c) => `K${c}`).join(", ") || "—"}</td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground whitespace-nowrap">{hhmm(p.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ScrollArea>
        </Card>

        <Card className="p-0 border border-border overflow-hidden" data-testid="rr-not-tried-list">
          <div className="p-4 pb-2 flex items-center gap-2">
            <UserX className="h-4 w-4 text-red-500" />
            <h4 className="font-display font-semibold text-base">Hiç denemeyen görevliler</h4>
          </div>
          <ScrollArea className="h-[340px]">
            {d.not_tried_officers.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">
                {d.totals.officers_total ? "Tüm görevliler çeteleyi denedi 👍" : "Henüz sandık görevlisi atanmadı."}
              </p>
            ) : d.not_tried_officers.map((o, i) => (
              <div key={i} className="flex items-center justify-between gap-2 border-t border-border px-4 py-2 text-sm"
                data-testid={`rr-untried-${i}`}>
                <span>
                  {o.name}
                  <span className="block text-[11px] text-muted-foreground">K{o.committee_no} {o.committee_name}</span>
                </span>
                <Button size="sm" variant="ghost" className="h-7 text-xs"
                  data-testid={`rr-card-${i}`}
                  onClick={() => download(`/election-day/officer-cards?committee_no=${o.committee_no}`,
                    `GOREVLI_KARTI_K${o.committee_no}.pdf`)}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Kart
                </Button>
              </div>
            ))}
          </ScrollArea>
        </Card>
      </div>

      <Card className="p-0 border border-border overflow-hidden" data-testid="rr-committee-table">
        <div className="p-4 pb-2">
          <h4 className="font-display font-semibold text-base">Komite bazlı</h4>
        </div>
        <ScrollArea className="h-[400px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/95 text-xs">
              <tr className="text-left">
                {["K", "Komite", "Durum", "İşaretleme", "Görevliler", "Deneyenler", "Son işlem"].map((h) => (
                  <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.committees.map((c) => (
                <tr key={c.committee_no} className="border-t border-border" data-testid={`rr-com-${c.committee_no}`}>
                  <td className="px-2 py-1.5 font-semibold">{c.committee_no}</td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate">{c.name}</td>
                  <td className="px-2 py-1.5">
                    {c.tried
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Denendi</Badge>
                      : <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]"><XCircle className="h-3 w-3 mr-1" />Denenmedi</Badge>}
                  </td>
                  <td className="px-2 py-1.5 tabular-nums">{c.attempts}</td>
                  <td className="px-2 py-1.5 text-xs">{c.officers.join(", ") || "—"}</td>
                  <td className="px-2 py-1.5 text-xs">{c.participants.join(", ") || "—"}</td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground whitespace-nowrap">{hhmm(c.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </Card>
    </div>
  );
}
